import { ErrorMessage, useFormikContext } from 'formik';
import { useState } from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@src/design-system/tooltip';
import type { TField } from '@src/shared/types/field';
import type { TReminderFormValues } from './schema';

type ProgramQuestionSelectProps = {
  dateFields: TField[];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
};

export function ProgramQuestionSelect({
  dateFields,
  placeholder = 'Question',
  searchPlaceholder = 'Search for questions',
}: ProgramQuestionSelectProps) {
  const [open, setOpen] = useState(false);
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<TReminderFormValues>();

  const disabled = !dateFields.length;
  const selectedField = dateFields.find(
    (field) => String(field.id) === String(values.question)
  );
  const selectedLabel = selectedField
    ? String(selectedField.title)
    : placeholder;

  const handleSelect = (fieldId: number) => {
    setFieldValue('question', String(fieldId));
    setFieldTouched('question', true, false);
    setOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!disabled) setOpen(nextOpen);
  };

  const popoverTrigger = (
    <PopoverTrigger
      type='button'
      disabled={disabled}
      {...(errors.question && touched.question && { 'data-error': 'error' })}
      className={cn(
        inputVariants({ variant: 'fill', size: 'lg' }),
        'mt-0 flex w-full cursor-pointer items-center justify-between text-sm',
        !selectedField && 'text-gray-400',
        disabled && 'cursor-not-allowed opacity-70'
      )}
    >
      <span className='truncate'>{selectedLabel}</span>
    </PopoverTrigger>
  );

  return (
    <div>
      <Popover
        modal
        open={disabled ? false : open}
        onOpenChange={handleOpenChange}
      >
        {disabled ? (
          <Tooltip>
            <TooltipTrigger asChild>{popoverTrigger}</TooltipTrigger>
            <TooltipContent>
              No date type questions for this program found
            </TooltipContent>
          </Tooltip>
        ) : (
          popoverTrigger
        )}
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] p-0'
        >
          <Command>
            <CommandInput
              containerClassName='rounded-none mb-0 border-0 border-b-1 border-pale-border'
              placeholder={searchPlaceholder}
            />
            <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
              <CommandEmpty>No questions found</CommandEmpty>
              <CommandGroup className='p-0'>
                {dateFields.map((field) => {
                  const fieldLabel = String(field.title);
                  const isSelected =
                    String(field.id) === String(values.question);

                  return (
                    <CommandItem
                      key={field.id}
                      value={fieldLabel}
                      className={cn(
                        'py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none',
                        isSelected && 'bg-primary-50'
                      )}
                      data-selected={false}
                      onSelect={() => handleSelect(field.id)}
                    >
                      <p
                        className={cn(
                          'text-sm text-gray-900 truncate',
                          isSelected && 'font-medium text-primary-1000'
                        )}
                      >
                        {fieldLabel}
                      </p>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='question'
        component={CustomErrorMessage}
      />
    </div>
  );
}
